import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import WithRoutify from '../../hoc/WithRoutify';
import withReduxState from '../../hoc/wReduxState';
import FormInputs from '../../components/UI/FormInputs';
import TableGrid from '../../components/UI/TableGrid';
import Buttons from '../../components/UI/Buttons';
import {
  fetchRxntSettings,
  voidPrescription,
  refillPrescription,
  reinstatePrescription,
} from '../../store/RxntSlice';
import RxntService from '../../services/RxntService';
import { OK } from '../../utils/constant';
import PERMISSIONS from '../../utils/AclPermissions';
import {
  canShowVoidForGatewayRow,
  rowWorklistId,
} from '../../utils/rxntUiUtils';
import RxntLaunchButton from '../../components/Rxnt/RxntLaunchButton';
import { RXNT_VOID_CODE_OPTIONS, RXNT_VOID_CODE_PLACEHOLDER } from '../../utils/rxntVoidCodes';
import GlobalSearch from '../../components/Layouts/NewAppLayout/GlobalSearch/GlobalSearch';

const RXNT_GLOBAL_FILTER_KEY = 'RXNT Prescriptions';

const GLOBAL_SEARCH_PATHS = ['/result/rxnt-prescriptions'];

/** Reinstate action + modal (Voided tab). Flip to true when ready to expose again. */
const RXNT_REINSTATE_UI_ENABLED = false;

/** Background refresh while Results RXNT page is open (tab counts + active list). 0 = disable polling. */
const RXNT_RESULTS_POLL_INTERVAL_MS = 30 * 1000;

const TABS = [
  { id: 'pending', label: 'Pending' },
  { id: 'recent', label: 'Recent' },
  { id: 'refill', label: 'Refills' },
  { id: 'voided', label: 'Voided' },
];

const VOID_CODE_SELECT_OPTIONS = [RXNT_VOID_CODE_PLACEHOLDER, ...RXNT_VOID_CODE_OPTIONS];

const TABLE_HEADERS = [
  { label: 'Patient', key: 'patient_name', sortName: 'patient_name' },
  { label: 'Visit / Rx ID', key: 'visit_rx', sortName: 'visit_rx' },
  { label: 'Medication', key: 'medication', sortName: 'medication' },
  { label: 'Pharmacy', key: 'pharmacy', sortName: 'pharmacy' },
  { label: 'Provider', key: 'provider_name', sortName: 'provider_name' },
  { label: 'Status', key: 'status_label', sortName: 'status_label' },
  { label: 'Date', key: 'date_display', sortName: 'date_display' },
  { label: 'Action', key: 'action', type: 'Actions' },
];

const DEFAULT_TABLE_PAGINATION = {
  page: 1,
  perPage: 25,
  totalRecords: 0,
  sort_by: 'date_display',
  sort_order: 'desc',
};

function ensureArray(d) {
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  return [];
}

function gatewayPatientName(rx) {
  const p = rx?.patient;
  if (!p) return '—';
  if (p.fullName) return p.fullName;
  return [p.firstName, p.lastName].filter(Boolean).join(' ') || '—';
}

function gatewayPharmacyName(rx) {
  return rx?.pharmacy?.name || '—';
}

function gatewayDrugLabel(rx) {
  return rx?.drug?.drugName || '—';
}

function gatewayProviderName(rx) {
  const d = rx?.doctor;
  if (!d) return '—';
  return [d.firstName, d.lastName].filter(Boolean).join(' ') || '—';
}

function gatewayStatusLabel(rx, worklist) {
  const st = rx?.prescriptionStatus;
  if (Array.isArray(st) && st.length > 0) {
    const x = st[0];
    if (typeof x === 'string') return x;
    if (x && typeof x === 'object') {
      return x.statusLabel || x.name || x.description || '—';
    }
  }
  if (worklist === 'voided') return 'Voided';
  if (worklist === 'refill' || worklist === 'refills') return 'Refill';
  if (worklist === 'pending') return 'Pending';
  if (worklist === 'recent') return 'Recent';
  return '—';
}

function gatewayDate(rx, worklist) {
  if (worklist === 'voided' && rx?.voidedDate) return rx.voidedDate;
  if (rx?.entryDate) return rx.entryDate;
  if (rx?.approvedDate) return rx.approvedDate;
  if (rx?.lastFilledDate) return rx.lastFilledDate;
  return null;
}

function formatDateString(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function parseRxDate(value) {
  if (!value) return null;
  const ts = Date.parse(value);
  return Number.isFinite(ts) ? ts : null;
}

function getRowSortValue(item, sortBy, tab) {
  const rx = item.rx || {};
  const local = item.local || {};
  switch (sortBy) {
    case 'patient_name':
      return gatewayPatientName(rx).toLowerCase();
    case 'visit_rx': {
      const rxNum = Number(rx.prescriptionId);
      if (Number.isFinite(rxNum)) return rxNum;
      return String(rx.prescriptionId || local?.visit_id || '').toLowerCase();
    }
    case 'medication':
      return gatewayDrugLabel(rx).toLowerCase();
    case 'pharmacy':
      return gatewayPharmacyName(rx).toLowerCase();
    case 'provider_name':
      return gatewayProviderName(rx).toLowerCase();
    case 'status_label':
      return (local.status || gatewayStatusLabel(rx, tab) || '').toLowerCase();
    case 'date_display':
    default: {
      const ts = parseRxDate(gatewayDate(rx, tab));
      return ts != null ? ts : 0;
    }
  }
}

function compareRows(a, b, sortBy, sortOrder, tab) {
  const va = getRowSortValue(a, sortBy, tab);
  const vb = getRowSortValue(b, sortBy, tab);
  let cmp = 0;
  if (typeof va === 'number' && typeof vb === 'number') {
    cmp = va - vb;
  } else {
    cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
  }
  return sortOrder === 'asc' ? cmp : -cmp;
}

function matchesGlobalFilter(item, gf, tab) {
  if (!gf || Object.keys(gf).length === 0) return true;
  const rx = item.rx || {};
  const local = item.local || {};

  if (gf.employee_id) {
    const empMatch =
      (local.employee_id && String(local.employee_id) === String(gf.employee_id)) ||
      (gf.searchName &&
        gatewayPatientName(rx).toLowerCase().includes(String(gf.searchName).toLowerCase()));
    if (!empMatch) return false;
  }

  const pid = (gf.patient_id || '').trim().toLowerCase();
  if (pid) {
    const patientRef = String(
      local.employee_id || rx.patientId || rx.patient?.patientId || ''
    ).toLowerCase();
    if (!patientRef.includes(pid)) return false;
  }

  const med = (gf.medication_search || '').trim().toLowerCase();
  if (med) {
    const blob = [gatewayDrugLabel(rx), local.drug_name, local.drug_strength]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (!blob.includes(med)) return false;
  }

  const pharmacy = (gf.pharmacy_name || '').trim().toLowerCase();
  if (pharmacy && !gatewayPharmacyName(rx).toLowerCase().includes(pharmacy)) return false;

  const rxId = (gf.rxnt_prescription_id || '').trim();
  if (rxId) {
    const rowRxId = String(rx.prescriptionId || local.rxnt_prescription_id || '');
    if (rowRxId !== rxId) return false;
  }

  if (gf.status) {
    const localSt = (local.status || '').toUpperCase();
    if (localSt && localSt !== String(gf.status).toUpperCase()) return false;
  }

  const ts = parseRxDate(gatewayDate(rx, tab));
  const fromTs = parseRxDate(gf.date_from);
  const toTs = parseRxDate(gf.date_to);
  if (fromTs != null && ts != null && ts < fromTs) return false;
  if (toTs != null && ts != null && ts > toTs + 86400000 - 1) return false;

  return true;
}

function getPrintErrorMessage(err, response) {
  if (response && typeof response.message === 'string' && response.message.trim()) {
    return response.message;
  }
  const fromAxios = err?.response?.data?.message;
  if (typeof fromAxios === 'string' && fromAxios.trim()) {
    return fromAxios;
  }
  if (typeof err === 'string' && err) return err;
  if (err?.message && !String(err.message).includes('status code')) {
    return err.message;
  }
  return 'Print failed';
}

const RxntPrescriptionsResult = ({ Toast, Loader, globalFilter, profile, location, isNewUI }) => {
  const dispatch = useDispatch();
  const { settings } = useSelector((s) => s.rxnt);
  const ERROR_TOAST_TIMEOUT = 6000;

  const [tab, setTab] = useState('pending');
  const [providerId, setProviderId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [mappedProviders, setMappedProviders] = useState([]);
  const [rows, setRows] = useState([]);
  const [listMeta, setListMeta] = useState(null);
  /** Gateway tab id → prescription count from API meta.total */
  const [tabCounts, setTabCounts] = useState({});
  const [error, setError] = useState(null);

  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidComments, setVoidComments] = useState('');
  const [voidCode, setVoidCode] = useState('');
  const [voidLocalRow, setVoidLocalRow] = useState(null);
  const [reinstateModalOpen, setReinstateModalOpen] = useState(false);
  const [reinstateLocalRow, setReinstateLocalRow] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printImage, setPrintImage] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [printFor, setPrintFor] = useState(null);
  const [tablePagination, setTablePagination] = useState({ ...DEFAULT_TABLE_PAGINATION });

  const handlersRef = useRef({});

  const selectedRxntDoctorId = (() => {
    const m = mappedProviders.find((x) => x.provider_id === providerId);
    return m?.rxnt_doctor_id != null ? Number(m.rxnt_doctor_id) : null;
  })();

  const providerOptions = useMemo(() => {
    const first = { value: '', label: '— Select mapped provider —' };
    const rest = mappedProviders.map((m) => {
      const pr = m.provider || {};
      const label =
        `${pr.first_name || ''} ${pr.last_name || ''}`.trim() || m.provider_id || 'Provider';
      return { value: m.provider_id, label };
    });
    return [first, ...rest];
  }, [mappedProviders]);

  const loadPrescriptions = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (settings.loading) {
      return;
    }
    if (!settings.rx_enabled) {
      setRows([]);
      setListMeta(null);
      setTabCounts({});
      return;
    }

    if (!providerId) {
      setRows([]);
      setListMeta(null);
      setTabCounts({});
      setError(null);
      return;
    }

    setError(null);
    if (!silent && Loader) Loader(true);
    try {
      const params = { tab, per_page: 300 };
      if (providerId) {
        params.provider_id = providerId;
      }
      if (selectedRxntDoctorId) {
        params.rxnt_doctor_id = selectedRxntDoctorId;
      }

      const res = await RxntService.listPrescriptions(params);
      if (res.code === OK) {
        setRows(Array.isArray(res.data) ? res.data : []);
        setListMeta(res.meta || null);
        const total =
          typeof res.meta?.total === 'number'
            ? res.meta.total
            : Array.isArray(res.data)
              ? res.data.length
              : 0;
        setTabCounts((prev) => ({ ...prev, [tab]: total }));
      } else {
        setError(res.message || 'Failed to load');
        setRows([]);
        setListMeta(null);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load prescriptions');
      setRows([]);
      setListMeta(null);
    } finally {
      if (!silent && Loader) Loader(false);
    }
  }, [tab, providerId, selectedRxntDoctorId, settings.loading, settings.rx_enabled, Loader]);

  /** Counts for tabs other than the active one (same calls as switching tabs). */
  const fetchInactiveTabCounts = useCallback(async () => {
    if (!providerId || selectedRxntDoctorId == null || !settings.rx_enabled || settings.loading) {
      return;
    }
    const inactive = TABS.filter((x) => x.id !== tab);
    if (inactive.length === 0) return;

    const base = {
      provider_id: providerId,
      rxnt_doctor_id: selectedRxntDoctorId,
      per_page: 300,
    };

    try {
      const results = await Promise.all(
        inactive.map((t) => RxntService.listPrescriptions({ ...base, tab: t.id }))
      );
      setTabCounts((prev) => {
        const next = { ...prev };
        inactive.forEach((t, i) => {
          const res = results[i];
          if (res.code === OK) {
            next[t.id] =
              typeof res.meta?.total === 'number'
                ? res.meta.total
                : Array.isArray(res.data)
                  ? res.data.length
                  : 0;
          }
        });
        return next;
      });
    } catch {
      // ignore background failures
    }
  }, [providerId, selectedRxntDoctorId, tab, settings.rx_enabled, settings.loading]);

  useEffect(() => {
    dispatch(fetchRxntSettings());
  }, [dispatch]);

  useEffect(() => {
    if (!Loader) return;
    if (settings.loading) {
      Loader(true);
    } else {
      Loader(false);
    }
    return () => {
      if (Loader) Loader(false);
    };
  }, [settings.loading, Loader]);

  useEffect(() => {
    (async () => {
      try {
        const res = await RxntService.getProviders();
        if (res.code === OK) {
          setMappedProviders(ensureArray(res.data));
        } else {
          setMappedProviders([]);
        }
      } catch (e) {
        setMappedProviders([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (mappedProviders.length !== 1) {
      return;
    }
    const id = mappedProviders[0]?.provider_id;
    if (id == null || id === '') {
      return;
    }
    setProviderId(String(id));
  }, [mappedProviders]);

  useEffect(() => {
    loadPrescriptions();
  }, [loadPrescriptions]);

  useEffect(() => {
    fetchInactiveTabCounts();
  }, [fetchInactiveTabCounts]);

  /** Poll RXNT while page is open so tab badges + grid approach live gateway totals (RXNT has no browser push). */
  useEffect(() => {
    if (
      !RXNT_RESULTS_POLL_INTERVAL_MS ||
      !providerId ||
      selectedRxntDoctorId == null ||
      !settings.rx_enabled ||
      settings.loading
    ) {
      return undefined;
    }
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      loadPrescriptions({ silent: true });
      fetchInactiveTabCounts();
    };
    const id = window.setInterval(tick, RXNT_RESULTS_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [
    providerId,
    selectedRxntDoctorId,
    settings.rx_enabled,
    settings.loading,
    loadPrescriptions,
    fetchInactiveTabCounts,
  ]);

  /** Refresh when user returns to this browser tab (RXNT may have changed while away). */
  useEffect(() => {
    if (!providerId || selectedRxntDoctorId == null || !settings.rx_enabled) {
      return undefined;
    }
    const onVisibility = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
      loadPrescriptions({ silent: true });
      fetchInactiveTabCounts();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [providerId, selectedRxntDoctorId, settings.rx_enabled, loadPrescriptions, fetchInactiveTabCounts]);

  const applySearchHandler = useCallback(() => {
    setAppliedSearch((searchInput || '').trim());
    loadPrescriptions();
  }, [searchInput, loadPrescriptions]);

  const clearFiltersHandler = useCallback(() => {
    setSearchInput('');
    setAppliedSearch('');
    loadPrescriptions();
  }, [loadPrescriptions]);

  const searchKeyDownHandler = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applySearchHandler();
    }
  };

  const openVoid = (localPrescription) => {
    if (!localPrescription?.id) {
      if (Toast) {
        Toast.error(
          'This row is not linked to a prescription in Snap. Use the RXNT portal to void if needed.',
          ERROR_TOAST_TIMEOUT
        );
      }
      return;
    }
    setVoidLocalRow(localPrescription);
    setVoidComments('');
    setVoidCode('');
    setVoidModalOpen(true);
  };

  const handleVoidConfirm = async () => {
    if (!voidCode) {
      if (Toast) Toast.error('Please select a void reason', ERROR_TOAST_TIMEOUT);
      return;
    }
    if (!voidComments.trim()) {
      if (Toast) Toast.error('Please enter comments for voiding', ERROR_TOAST_TIMEOUT);
      return;
    }
    if (!voidLocalRow?.id) return;

    setActionLoading(true);
    try {
      if (Loader) Loader(true);
      await dispatch(
        voidPrescription({
          prescriptionId: voidLocalRow.id,
          comments: voidComments.trim(),
          voidCode,
        })
      ).unwrap();
      if (Toast) Toast.success('Prescription voided');
      setVoidModalOpen(false);
      setVoidLocalRow(null);
      loadPrescriptions();
    } catch (err) {
      if (Toast) {
        Toast.error(
          typeof err === 'string' ? err : err?.message || 'Failed to void',
          ERROR_TOAST_TIMEOUT
        );
      }
    } finally {
      setActionLoading(false);
      if (Loader) Loader(false);
    }
  };

  const handleRefill = async (localPrescription) => {
    if (!localPrescription?.id) {
      if (Toast) Toast.error('Link this prescription in Snap to request refill here.', ERROR_TOAST_TIMEOUT);
      return;
    }
    if (!window.confirm('Request a refill for this prescription?')) return;
    setActionLoading(true);
    try {
      if (Loader) Loader(true);
      await dispatch(refillPrescription(localPrescription.id)).unwrap();
      if (Toast) Toast.success('Refill requested');
      loadPrescriptions();
    } catch (err) {
      if (Toast) {
        Toast.error(
          typeof err === 'string' ? err : err?.message || 'Refill failed',
          ERROR_TOAST_TIMEOUT
        );
      }
    } finally {
      setActionLoading(false);
      if (Loader) Loader(false);
    }
  };

  const openReinstate = (localPrescription) => {
    if (!localPrescription?.id) return;
    setReinstateLocalRow(localPrescription);
    setReinstateModalOpen(true);
  };

  const handleReinstateConfirm = async () => {
    if (!reinstateLocalRow?.id) return;
    setActionLoading(true);
    try {
      if (Loader) Loader(true);
      await dispatch(reinstatePrescription(reinstateLocalRow.id)).unwrap();
      if (Toast) Toast.success('Prescription reinstated');
      setReinstateModalOpen(false);
      setReinstateLocalRow(null);
      loadPrescriptions();
    } catch (err) {
      if (Toast) {
        Toast.error(
          typeof err === 'string' ? err : err?.message || 'Reinstate failed',
          ERROR_TOAST_TIMEOUT
        );
      }
    } finally {
      setActionLoading(false);
      if (Loader) Loader(false);
    }
  };

  const runPrintLocal = async (localPrescription) => {
    if (!localPrescription?.rxnt_prescription_id) {
      if (Toast) Toast.error('Not yet sent to RXNT', ERROR_TOAST_TIMEOUT);
      return;
    }
    setPrintLoading(true);
    setPrintFor({ kind: 'local', local: localPrescription });
    if (Loader) Loader(true);
    try {
      const response = await RxntService.printPrescription(localPrescription.id);
      if (response.code === OK && response.data?.image) {
        setPrintImage(response.data.image);
        setPrintModalOpen(true);
      } else if (Toast) {
        Toast.error(getPrintErrorMessage(null, response), ERROR_TOAST_TIMEOUT);
      }
    } catch (err) {
      if (Toast) {
        Toast.error(getPrintErrorMessage(err, null), ERROR_TOAST_TIMEOUT);
      }
    } finally {
      setPrintLoading(false);
      if (Loader) Loader(false);
    }
  };

  const runPrintGatewayOnly = async (rxntPrescriptionId, rx) => {
    if (!selectedRxntDoctorId) {
      if (Toast) Toast.error('Select a mapped provider (doctor).', ERROR_TOAST_TIMEOUT);
      return;
    }
    setPrintLoading(true);
    setPrintFor({
      kind: 'gateway',
      rxntPrescriptionId,
      drugName: rx ? gatewayDrugLabel(rx) : 'rx',
    });
    if (Loader) Loader(true);
    try {
      const response = await RxntService.printPrescriptionGateway(
        rxntPrescriptionId,
        selectedRxntDoctorId
      );
      if (response.code === OK && response.data?.image) {
        setPrintImage(response.data.image);
        setPrintModalOpen(true);
      } else if (Toast) {
        Toast.error(getPrintErrorMessage(null, response), ERROR_TOAST_TIMEOUT);
      }
    } catch (err) {
      if (Toast) {
        Toast.error(getPrintErrorMessage(err, null), ERROR_TOAST_TIMEOUT);
      }
    } finally {
      setPrintLoading(false);
      if (Loader) Loader(false);
    }
  };

  const getPrintDrugName = () => {
    if (!printFor) return 'Print';
    if (printFor.kind === 'local') return printFor.local?.drug_name || 'Print';
    return printFor.drugName || 'Print';
  };

  const handleDownloadPrint = () => {
    if (!printImage || !printFor) return;
    const name = getPrintDrugName();
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${printImage}`;
    link.download = `prescription_${name}_${new Date().toISOString().slice(0, 10)}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClosePrintModal = () => {
    setPrintModalOpen(false);
    setPrintImage(null);
    setPrintFor(null);
  };

  const handleBrowserPrint = () => {
    if (!printImage) return;
    const drugName = getPrintDrugName();
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      if (Toast) {
        Toast.error('Pop-up blocked. Allow pop-ups to print.', ERROR_TOAST_TIMEOUT);
      }
      return;
    }
    printWindow.document.write(`
      <html>
        <head><title>Prescription - ${drugName}</title></head>
        <body style="margin:0; display:flex; justify-content:center; align-items:center;">
          <img src="data:image/png;base64,${printImage}" style="max-width:100%;" onload="window.print();window.close();" />
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  handlersRef.current = {
    openVoid,
    handleRefill,
    openReinstate,
    runPrintLocal,
    runPrintGatewayOnly,
    selectedRxntDoctorId,
    tab,
    listMeta,
    actionLoading,
    printLoading,
    printFor,
  };

  const rxntGlobalFilter = globalFilter?.[RXNT_GLOBAL_FILTER_KEY] || {};
  const rxntGlobalFilterKey = JSON.stringify(rxntGlobalFilter);

  const filteredRows = useMemo(() => {
    let list = rows.filter((item) => matchesGlobalFilter(item, rxntGlobalFilter, tab));
    const q = (appliedSearch || '').trim().toLowerCase();
    if (!q) return list;
    return list.filter((item) => {
      const rx = item.rx || {};
      const blob = [
        gatewayPatientName(rx),
        gatewayDrugLabel(rx),
        gatewayPharmacyName(rx),
        String(rx.prescriptionId || ''),
        gatewayProviderName(rx),
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [rows, appliedSearch, rxntGlobalFilter, tab]);

  useEffect(() => {
    setTablePagination((prev) => ({ ...prev, page: 1 }));
  }, [tab, appliedSearch, rxntGlobalFilterKey]);

  const sortedRows = useMemo(() => {
    const sortBy = tablePagination.sort_by || 'date_display';
    const sortOrder = tablePagination.sort_order || 'desc';
    return [...filteredRows].sort((a, b) => compareRows(a, b, sortBy, sortOrder, tab));
  }, [filteredRows, tablePagination.sort_by, tablePagination.sort_order, tab]);

  const handleTablePagination = useCallback((newPagination) => {
    setTablePagination((prev) => ({
      ...prev,
      ...newPagination,
    }));
  }, []);

  const pathname = location?.pathname || '/result/rxnt-prescriptions';
  const showGlobalSearch =
    isNewUI &&
    profile &&
    profile.is_multiple_organization === false &&
    profile.employee_login === false &&
    GLOBAL_SEARCH_PATHS.some((path) => pathname.startsWith(path));

  const tableRecords = useMemo(() => {
    const h = handlersRef;
    const tableData = [];

    sortedRows.forEach((item, index) => {
      const rx = item.rx || {};
      const local = item.local;
      const rxId = rx.prescriptionId;
      const rowId = `gw-${rxId}-${index}`;

      const actionBtns = [];
      const rowWorklist = rowWorklistId(item, tab);
      if (canShowVoidForGatewayRow(item, tab)) {
        actionBtns.push({
          className: 'btn btn-icon btn-transparent-dark',
          iconType: 'Remove',
          title: 'Void',
          acl: PERMISSIONS.RESULT.LIST,
          disabled: h.current.actionLoading,
          clickHandler: () => h.current.openVoid(local),
        });
      }
      if (
        tab === 'refill' &&
        local &&
        (local.status === 'DELIVERED' || local.status === 'FILLED')
      ) {
        actionBtns.push({
          className: 'btn btn-icon btn-transparent-dark',
          iconType: 'AddPlus',
          title: 'Refill',
          acl: PERMISSIONS.RESULT.LIST,
          disabled: h.current.actionLoading,
          clickHandler: () => h.current.handleRefill(local),
        });
      }
      if (
        RXNT_REINSTATE_UI_ENABLED &&
        tab === 'voided' &&
        local &&
        local.status === 'VOIDED'
      ) {
        actionBtns.push({
          className: 'btn btn-icon btn-transparent-dark',
          iconType: 'Update',
          title: 'Reinstate',
          acl: PERMISSIONS.RESULT.LIST,
          disabled: h.current.actionLoading,
          clickHandler: () => h.current.openReinstate(local),
        });
      }
      if (rxId) {
        const printingThis =
          h.current.printLoading &&
          ((h.current.printFor?.kind === 'local' &&
            local &&
            h.current.printFor.local?.id === local.id) ||
            (h.current.printFor?.kind === 'gateway' &&
              h.current.printFor.rxntPrescriptionId === rxId));
        actionBtns.push({
          className: 'btn btn-icon btn-transparent-dark',
          iconType: 'Print',
          title: printingThis ? 'Loading...' : 'Print',
          acl: PERMISSIONS.RESULT.LIST,
          disabled: h.current.actionLoading || h.current.printLoading,
          clickHandler: () =>
            local
              ? h.current.runPrintLocal(local)
              : h.current.runPrintGatewayOnly(rxId, rx),
        });
      }

      const visitRxText = local?.visit_id
        ? `RXNT #${rxId} · Visit ${local.visit_id}`
        : `RXNT #${rxId}`;
      tableData.push({
        isHeading: false,
        rowId,
        data: {
          patient_name: gatewayPatientName(rx),
          visit_rx: visitRxText,
          medication: gatewayDrugLabel(rx),
          pharmacy: gatewayPharmacyName(rx),
          provider_name: gatewayProviderName(rx),
          status_label: gatewayStatusLabel(rx, rowWorklist),
          date_display: formatDateString(gatewayDate(rx, rowWorklist)),
          action: actionBtns,
        },
      });
    });

    return {
      tableHeaders: TABLE_HEADERS,
      tableRows: {
        status: 200,
        type: 'default',
        data: tableData,
      },
      pagination: {
        page: tablePagination.page || 1,
        perPage: tablePagination.perPage || 25,
        totalRecords: tableData.length,
        sort_by: tablePagination.sort_by || 'date_display',
        sort_order: tablePagination.sort_order || 'desc',
      },
    };
  }, [sortedRows, tab, actionLoading, printLoading, printFor, tablePagination]);

  const gridEditProps = useMemo(
    () => ({ activeEditId: null, onChangeFormDataInEdit: () => {} }),
    []
  );

  if (settings.loading) {
    return null;
  }

  if (!settings.rx_enabled) {
    return (
      <div className="p-3 w-100">
        <div className="sh_cardBox position-relative">
          <div className="sh_cardBody p-4 text-center text-muted">
            <h3 className="h2-strong">RXNT ePrescribing Not Enabled</h3>
            <p>Contact your administrator to enable RXNT for this organization.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 w-100 h-100 d-flex flex-column rxnt-results-page">
      <div className="sh_cardBox rxnt-results-worklist-card position-relative d-flex flex-column flex-grow-1 overflow-hidden">
        <div className="sh_cardHeader pb-0 flex-shrink-0 mt-0 pt-0">
          <div className="d-flex flex-column w-100 mt-0">
            {/* Desktop (lg+): original single-row header layout */}
            <div className="d-none d-lg-flex position-relative align-items-center flex-wrap gap-2 w-100 sh_pagetopsubheader sh_rxntHeader">
              <h2 className="h2-strong position-absolute sh_welcome-text mb-0 sh_rxntHeaderTitle">
                RXNT Prescriptions
              </h2>
              {showGlobalSearch && (
                <div
                  className="d-flex justify-content-center align-items-center sh_pagetopsubheadersearch"
                  style={{ flex: 1 }}
                >
                  <GlobalSearch
                    pathname={pathname}
                  />
                </div>
              )}
              <div className="d-flex gap-2 flex-wrap ms-auto">
                {/* Provider-scoped launch (no patient context); uses selected results-page provider filter. */}
                <RxntLaunchButton
                  text="Open RXNT"
                  providerId={providerId}
                  rxntDoctorId={selectedRxntDoctorId}
                  onError={(message) => {
                    if (Toast) Toast.error(message, ERROR_TOAST_TIMEOUT);
                  }}
                />
              </div>
            </div>

            {/* Tablet/mobile: title + button on one line; search on second line */}
            <div className="d-flex d-lg-none flex-column w-100 sh_pagetopsubheader sh_rxntHeader sh_rxntHeaderTablet">
              <div className="d-flex align-items-center justify-content-between flex-nowrap gap-2 w-100 sh_rxntHeaderTabletRow">
                <h2
                  className="h2-strong sh_welcome-text mb-0 sh_rxntHeaderTitle flex-grow-1 text-truncate"
                  style={{ minWidth: 0 }}
                >
                  RXNT Prescriptions
                </h2>
                <div className="d-flex gap-2 flex-nowrap flex-shrink-0">
                  <RxntLaunchButton
                    text="Open RXNT"
                    providerId={providerId}
                    rxntDoctorId={selectedRxntDoctorId}
                    onError={(message) => {
                      if (Toast) Toast.error(message, ERROR_TOAST_TIMEOUT);
                    }}
                  />
                </div>
              </div>

              {showGlobalSearch && (
                <div className="d-flex align-items-center sh_pagetopsubheadersearch w-100 mt-2">
                  <GlobalSearch
                    pathname={pathname}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sh_cardBody sh_cardbglight pt-0 my-1 rxnt-results-card-main d-flex flex-column flex-grow-1 overflow-hidden">
          <div className="rxnt-results-toolbar-stack flex-shrink-0">
          <div className="d-flex flex-column w-100 mt-3">
            <div className="d-flex justify-content-between align-items-end flex-wrap gap-2 gap-md-3 border-bottom">
              <div className="tabs_wrapForTable flex-grow-1 min-w-0">
                <ul className="nav nav-borders nav-tabs border-0" role="tablist">
                  {TABS.map((t) => (
                    <li key={t.id} className="nav-item" role="presentation">
                      <a
                        className={`flex-sm-fill text-sm-center nav-link ${
                          tab === t.id ? 'active' : ''
                        }`}
                        href="#!"
                        onClick={(e) => {
                          e.preventDefault();
                          setTab(t.id);
                        }}
                        role="tab"
                        style={{ cursor: 'pointer' }}
                      >
                        {t.label}
                        {providerId &&
                          selectedRxntDoctorId != null &&
                          typeof tabCounts[t.id] === 'number' &&
                          ` (${tabCounts[t.id]})`}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div
                className="d-flex align-items-end flex-wrap flex-shrink-0 pb-1 ms-auto"
                style={{ gap: '10px' }}
              >
                <div style={{ minWidth: 220 }}>
                  <FormInputs
                    type="SelectList"
                    label="Provider"
                    name="rxnt_list_provider"
                    value={providerId}
                    changeHandler={(val) => setProviderId(val || '')}
                    options={providerOptions}
                    required
                    className="form-control form-control-solid"
                  />
                </div>
                <div style={{ minWidth: 100 }}>
                  <FormInputs
                    type="TextInput"
                    className="form-control form-control-solid"
                    label="Search"
                    placeholder="Search"
                    name="rxnt_list_search"
                    value={searchInput}
                    changeHandler={(val) => setSearchInput(val || '')}
                    onKeyDownHandler={searchKeyDownHandler}
                  />
                </div>
                <div className="d-flex align-items-end pb-1" style={{ gap: '8px' }}>
                  <button
                    type="button"
                    className="btn btn-primary sh_btnprimary"
                    onClick={applySearchHandler}
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={clearFiltersHandler}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>

          {!providerId && (
            <div className="alert alert-info py-2 small mb-0 mx-1">
              Choose an RXNT-mapped provider to load worklists (required <code>doctorId</code>).
            </div>
          )}

          {error && (
            <div className="alert alert-warning py-2 mx-1 my-2">{error}</div>
          )}

          </div>

          {/*
          {listMeta && (
            <div className="small text-muted mb-2 mx-1">
              EHR Gateway · doctorId {listMeta.rxnt_doctor_id} · {listMeta.total ?? filteredRows.length}{' '}
              record(s) · showing {filteredRows.length} after search
            </div>
          )}
          */}

          <div className="sh_cardBody p-0 sh_cardbglight rxnt-results-table-scroll border-top">
            <TableGrid
              isSort
              hasPagination={false}
              needCardBody={false}
              fetchTableRecords={handleTablePagination}
              gridEditProps={gridEditProps}
              customClass="rxnt-results-rx-tablegrid"
              {...tableRecords}
            />
          </div>
        </div>
      </div>

      <Modal
        show={voidModalOpen}
        onHide={() => {
          setVoidModalOpen(false);
          setVoidLocalRow(null);
        }}
        centered
        backdrop="static"
        className="sh_customModal"
      >
        <div className="sh_modalBox">
          <Modal.Header closeButton>
            <Modal.Title>
              <h6 className="text-lg mb-0 font-weight-bold">Void Prescription</h6>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              You are about to void the prescription for{' '}
              <strong>
                {[voidLocalRow?.drug_name, voidLocalRow?.drug_strength].filter(Boolean).join(' · ') || '—'}
              </strong>.
            </p>
            <div className="form-group">
              <label className="mb-1 text-sm opacity-75">
                Void reason <span className="text-danger">*</span>
              </label>
              <select
                className="form-control form-control-solid"
                value={voidCode}
                onChange={(e) => setVoidCode(e.target.value)}
                disabled={actionLoading}
              >
                {VOID_CODE_SELECT_OPTIONS.map((opt) => (
                  <option key={opt.value || 'placeholder'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="mb-1 text-sm opacity-75">
                Comments <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control form-control-solid"
                value={voidComments}
                onChange={(e) => setVoidComments(e.target.value)}
                placeholder="Enter additional comments for voiding this prescription..."
                rows={3}
                disabled={actionLoading}
              />
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-secondary sh_btnsecondary mr-2"
              onClick={() => {
                setVoidModalOpen(false);
                setVoidLocalRow(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary sh_btnprimary"
              onClick={handleVoidConfirm}
              disabled={actionLoading || !voidCode || !voidComments.trim()}
            >
              {actionLoading ? 'Voiding...' : 'Void Prescription'}
            </button>
          </Modal.Footer>
        </div>
      </Modal>

      {RXNT_REINSTATE_UI_ENABLED && (
        <Modal
          show={reinstateModalOpen}
          onHide={() => {
            setReinstateModalOpen(false);
            setReinstateLocalRow(null);
          }}
          centered
          backdrop="static"
          className="sh_customModal"
        >
          <div className="sh_modalBox">
            <Modal.Header closeButton>
              <Modal.Title>Reinstate prescription</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="mb-0">
                Reinstate{' '}
                <strong>{reinstateLocalRow?.drug_name || 'this prescription'}</strong>? This will restore the
                prescription in Snap and RXNT (when applicable).
              </p>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setReinstateModalOpen(false);
                  setReinstateLocalRow(null);
                }}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleReinstateConfirm}
                disabled={actionLoading}
              >
                {actionLoading ? 'Reinstating…' : 'Reinstate'}
              </button>
            </Modal.Footer>
          </div>
        </Modal>
      )}

      <Modal
        show={printModalOpen}
        onHide={handleClosePrintModal}
        centered
        size="lg"
        backdrop="static"
        className="sh_customModal"
      >
        <div className="sh_modalBox">
          <Modal.Header closeButton>
            <Modal.Title>
              <h6 className="text-lg mb-0 font-weight-bold">
                Prescription - {getPrintDrugName()}
              </h6>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            {printImage ? (
              <img
                src={`data:image/png;base64,${printImage}`}
                alt="Prescription"
                style={{ maxWidth: '100%', maxHeight: '70vh', border: '1px solid #ddd' }}
              />
            ) : (
              <div>Loading prescription image...</div>
            )}
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-center">
            <Buttons
              className="btn btn-secondary sh_btnsecondary mr-2"
              label="Close"
              clickHandler={handleClosePrintModal}
            />
            <Buttons
              className="btn btn-primary sh_btnprimary mr-2"
              label="Download"
              clickHandler={handleDownloadPrint}
              disabled={!printImage}
              acl={PERMISSIONS.RESULT.LIST}
            />
            <Buttons
              className="btn btn-primary sh_btnprimary"
              label="Print"
              clickHandler={handleBrowserPrint}
              disabled={!printImage}
              acl={PERMISSIONS.RESULT.LIST}
            />
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

export default withReduxState(WithRoutify(RxntPrescriptionsResult));
